import { useSelector } from "react-redux";
import Loader from "../Loader/Loader";
import PermissionDenied from "../PermissionDenied/PermissionDenied";
import { usePermissions } from "../../hooks/usePermissions";
import { getPageDeniedContent } from "../../permissions/permissionMessages";

/**
 * Route guard: full-page 403 when the user lacks permission(s).
 * Copy is centralized in {@link ../../permissions/permissionMessages.js}.
 * While the session is authenticated but GET /users/me has not settled (`profileResolved === false`),
 * or `fetchMe` is in flight, shows a centered loader — avoids a 403 flash before `role.permissions` exist.
 *
 * @param {object} props
 * @param {string|string[]} props.permission — one or more {@link ../../permissions/permissionKeys.js} values
 * @param {boolean} [props.requireAll=true] — if multiple keys, require all vs any
 * @param {import('react').ReactNode} props.children
 */
const RequirePermission = ({ permission, requireAll = true, children }) => {
  const user = useSelector((s) => s.auth?.user);
  const me = useSelector((s) => s.auth?.me);
  const profileResolved = useSelector((s) => s.auth?.profileResolved);
  const fetchMeLoading = useSelector((s) => s.auth?.loading?.fetchMe);
  const { can } = usePermissions();

  const keys = Array.isArray(permission) ? permission : [permission];

  const waitingForProfile = user != null && me == null && profileResolved === false;

  if (waitingForProfile || (fetchMeLoading && !me)) {
    return (
      <div className="flex items-center justify-center min-h-[50vh] w-full">
        <Loader />
      </div>
    );
  }

  const allowed = requireAll ? keys.every((k) => can(k)) : keys.some((k) => can(k));

  if (!allowed) {
    const missingKeys = keys.filter((k) => !can(k));
    return <PermissionDenied {...getPageDeniedContent(permission, requireAll, { missingKeys })} />;
  }

  return children;
};

export default RequirePermission;
